import type { VorgangPickerItem } from "../../../services/vorgangService";

/**
 * J5 — die gestufte Reihenfolge des Vorgänge-Reiters, rein und testbar.
 *
 * Reihenfolge ist immer: Vorgänge des Kontakts → meine und Team-Vorgänge →
 * alle. Sie bleibt auch bei aktiver Suche stehen, damit niemand raten muss,
 * warum ein Treffer oben steht.
 *
 * Die Daten liefert `GET /vorgang/picker` (Cluster VORGANG, D1–D3) mit genau
 * dieser Rangfolge, Suche und Offset-Paging — statt drei Feed-Aufrufe
 * nachzubauen, wird der eine gestufte Endpoint benutzt.
 */

export type StufenKey = "kontakt" | "meine" | "alle";

export type VorgangZeile = {
    id: number;
    titel: string;
    vorgangNbr?: string | null;
    status?: string | null;
    twv?: string | null;
    frist?: string | null;
    kundeLabel?: string | null;
    art: "vorgang" | "todo";
    /** Bei `art='todo'` der Eltern-Vorgang — dorthin führt der Klick. */
    vorgangId?: number;
};

/**
 * Dedupe-Schlüssel. Bewusst `art:id` und nicht `id` allein: Vorgänge und Todos
 * stammen aus getrennten Tabellen — ein Todo mit Id 42 darf einen Vorgang mit
 * Id 42 nicht verdrängen.
 */
export function zeilenKey(z: Pick<VorgangZeile, "art" | "id">): string {
    return `${z.art}:${z.id}`;
}

/** Rang des Picker-Endpoints → benannte Stufe. */
export function stufeAusRang(rang: number): StufenKey {
    if (rang <= 1) return "kontakt";
    if (rang === 2) return "meine";
    return "alle";
}

/** Ein Picker-Eintrag als Zeile — plus die (bei Suche) treffenden Todos. */
export function zuZeilen(it: VorgangPickerItem | null | undefined): VorgangZeile[] {
    const v = it?.vorgang;
    if (!v || typeof v.id !== "number") return []; // defensiv: unbrauchbarer Eintrag
    const zeilen: VorgangZeile[] = [
        {
            id: v.id,
            titel: v.titel ?? "Vorgang",
            vorgangNbr: v.vorgangNbr ?? null,
            status: v.status ?? null,
            twv: v.twv ?? null,
            frist: v.frist ?? null,
            kundeLabel: v.kundeName ?? null,
            art: "vorgang",
        },
    ];
    for (const t of it?.todos ?? []) {
        if (!t || typeof t.id !== "number") continue;
        zeilen.push({
            id: t.id,
            titel: t.titel ?? "To-do",
            status: t.status ?? null,
            art: "todo",
            vorgangId: v.id,
        });
    }
    return zeilen;
}

export type Stufe = {
    key: StufenKey;
    titel: string;
    zeilen: VorgangZeile[];
    /** Clientseitig gekürzt — „weitere anzeigen" ohne Netz. */
    mehrLokal: boolean;
};

/** Zeilen je Stufe, in der Reihenfolge des Servers. */
export type StufenZeilen = Record<StufenKey, VorgangZeile[]>;

/** Leere Ausgangsmenge — hält die Reihenfolge der Schlüssel fest. */
export function leereStufen(): StufenZeilen {
    return { kontakt: [], meine: [], alle: [] };
}

/**
 * Picker-Einträge auf die drei Stufen verteilen und global deduplizieren:
 * Was in einer HÖHEREN Stufe steht, verschwindet aus den tieferen.
 */
export function verteileAufStufen(items: readonly VorgangPickerItem[]): StufenZeilen {
    const out = leereStufen();
    const gesehen = new Set<string>();
    for (const it of items ?? []) {
        const stufe = stufeAusRang(it?.rang ?? 3);
        for (const z of zuZeilen(it)) {
            const key = zeilenKey(z);
            if (gesehen.has(key)) continue;
            gesehen.add(key);
            out[stufe].push(z);
        }
    }
    return out;
}

const TITEL: Record<StufenKey, string> = {
    kontakt: "Vorgänge dieser Mail",
    meine: "Meine und Team-Vorgänge",
    alle: "Alle Vorgänge",
};

/** Standard-Kürzung je Stufe; Stufe 1 zeigt der PO vollständig. */
export const LIMIT_JE_STUFE: Record<StufenKey, number> = { kontakt: 25, meine: 8, alle: 8 };

export function baueStufen(input: {
    zeilen: StufenZeilen;
    kontaktName: string | null;
    aufgeklappt: ReadonlySet<StufenKey>;
}): Stufe[] {
    const out: Stufe[] = [];
    for (const key of ["kontakt", "meine", "alle"] as const) {
        const alle = input.zeilen[key] ?? [];
        if (alle.length === 0) continue;
        const limit = LIMIT_JE_STUFE[key];
        const gekuerzt = !input.aufgeklappt.has(key) && alle.length > limit;
        out.push({
            key,
            titel:
                key === "kontakt" && input.kontaktName?.trim()
                    ? `Vorgänge von ${input.kontaktName.trim()}`
                    : TITEL[key],
            zeilen: gekuerzt ? alle.slice(0, limit) : alle,
            mehrLokal: gekuerzt,
        });
    }
    return out;
}
